import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDateTime } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import { StatusIcon, stringifyStatus } from '../../helpers/type-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  Query,
  SecurityTransitionListItem,
  Status,
  Subscription,
} from '../../logic/graphql/types/types.d';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const SecurityTransitionDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string = (match.params as any).id || '';

  const [securityTransitionQueryResult, reexecuteSecurityTransitionQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_security_transition.queryString,
    variables: GraphqlOperations.queries.get_security_transition.createVars(id),
    pause: id === '',
    requestPolicy: 'network-only',
  });
  const securityTransition = securityTransitionQueryResult.data?.get_security_transition;

  const handleUpdateSecurityTransitionSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionSecurityTransitions: SecurityTransitionListItem[] = [],
    response: Subscription,
  ): SecurityTransitionListItem[] => {
    if (response && response.update_security_transition) {
      if (
        response.update_security_transition.transaction_code ===
        securityTransitionQueryResult.data?.get_security_transition.transaction_code
      ) {
        reexecuteSecurityTransitionQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_security_transition.subscriptionString,
    },
    handleUpdateSecurityTransitionSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancelClick = (): void => {
    history.push(Paths.contact);
  };

  if (id === '') {
    return <Redirect to={Paths.security_transitions} />;
  }

  const title = securityTransition?.security.name
    ? securityTransition?.security.name +
      intl.formatMessage({ id: 'securityTransitions.details.titleSecurityExtension' })
    : intl.formatMessage({ id: 'securityTransitions.details.defaultTitle' });
  const titleWithTransactionCode = securityTransition?.transaction_code
    ? title + ' - ' + securityTransition.transaction_code
    : title;

  return (
    <DefaultScreen
      background={assets.images.orders_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={securityTransitionQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={securityTransition?.status} />
          <h2 className="ml-2">{stringifyStatus(securityTransition?.status, intl)}</h2>
        </aside>
      }
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="securityTransitions.details.dataLabel" />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'securityTransitions.details.fromAccountLabel' })}
            value={securityTransition?.from_account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransitions.details.toAccountLabel' })}
            value={securityTransition?.to_account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransitions.details.countLabel' })}
            value={formatNumber(
              securityTransition?.amount,
              intl.formatMessage({ id: 'securityTransitions.details.pcs' }),
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransitions.details.transitionDateLabel' })}
            value={formatDateTime(securityTransition?.transfer_date)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransitions.details.fulfillmentDateLabel' })}
            value={formatDateTime(securityTransition?.fulfillment_date)}
          />
        </BlurredContainerM2030>
      </div>
      {securityTransition?.status === Status.Active ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id="securityTransitions.details.cancelButton" />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(SecurityTransitionDetails, { fallback: Fallback });
